import { useCallback, useEffect, useState } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import { Headset, Loader2, RefreshCw, Save, Trash2 } from "lucide-react";

const defaultConfig = {
  pageTitle: "",
  pageSubtitle: "",
  contactPhone: "",
  contactEmail: "",
  emergencyNote: "",
  isActive: true,
};

const statuses = ["open", "in-progress", "resolved", "closed"];

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 });
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const fetchTickets = useCallback(async (nextPage = page) => {
    try {
      setLoadingTickets(true);
      const response = await API.get("/admin/support", {
        params: { page: nextPage, limit: 12, status, search },
      });
      const payload = response.data || {};
      setTickets(payload.tickets || []);
      setStats(payload.stats || { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 });
      setPage(payload.page || 1);
      setTotalPages(payload.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load support tickets");
    } finally {
      setLoadingTickets(false);
    }
  }, [page, search, status]);

  const fetchConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const response = await API.get("/admin/support/config");
      setConfig({ ...defaultConfig, ...(response.data?.config || {}) });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load support config");
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets(1);
  }, [status, search, fetchTickets]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === tickets.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(tickets.map((t) => t._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "support",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccess("Batch delete completed");
        fetchTickets(page);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleStatusUpdate = async (id, nextStatus) => {
    setError("");
    setSuccess("");
    try {
      await API.patch(`/admin/support/${id}/status`, { status: nextStatus });
      setSuccess("Ticket status updated");
      fetchTickets(page);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update ticket status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this support ticket?")) return;
    setError("");
    setSuccess("");
    try {
      await API.delete(`/admin/support/${id}`);
      setSuccess("Ticket deleted");
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      fetchTickets(page);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete ticket");
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      setSavingConfig(true);
      await API.put("/admin/support/config", config);
      setSuccess("Support config updated");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update support config");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Support</h1>
          <p className="text-sm text-gray-500">Manage support contact info and tickets</p>
        </div>
        <button
          onClick={() => {
            fetchConfig();
            fetchTickets();
          }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 text-sm rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 text-sm rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
          {success}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard title="Total" value={stats.total} />
        <StatCard title="Open" value={stats.open} />
        <StatCard title="In Progress" value={stats.inProgress} />
        <StatCard title="Resolved" value={stats.resolved} />
        <StatCard title="Closed" value={stats.closed} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <form
          onSubmit={handleSaveConfig}
          className="xl:col-span-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 space-y-4"
        >
          <div className="flex items-center gap-2">
            <Headset className="w-5 h-5 text-pink-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Details</h2>
          </div>

          <Field
            label="Page Title"
            value={config.pageTitle}
            onChange={(pageTitle) => setConfig((prev) => ({ ...prev, pageTitle }))}
          />
          <Field
            label="Page Subtitle"
            value={config.pageSubtitle}
            onChange={(pageSubtitle) => setConfig((prev) => ({ ...prev, pageSubtitle }))}
          />
          <Field
            label="Contact Phone"
            value={config.contactPhone}
            onChange={(contactPhone) => setConfig((prev) => ({ ...prev, contactPhone }))}
          />
          <Field
            label="Contact Email"
            value={config.contactEmail}
            onChange={(contactEmail) => setConfig((prev) => ({ ...prev, contactEmail }))}
          />
          <Field
            label="Emergency Note"
            value={config.emergencyNote}
            multiline
            onChange={(emergencyNote) => setConfig((prev) => ({ ...prev, emergencyNote }))}
          />

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={Boolean(config.isActive)}
              onChange={(e) => setConfig((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="rounded"
            />
            Support Channel Active
          </label>

          <button
            type="submit"
            disabled={savingConfig || loadingConfig}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-pink-500 hover:bg-pink-600 text-white font-medium text-sm disabled:opacity-50"
          >
            {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Configuration
          </button>
        </form>

        <div className="xl:col-span-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Support Tickets</h2>
            <div className="flex gap-2">
              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search query or feedback"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent text-sm"
              />
              <select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent text-sm"
              >
                <option value="">All Status</option>
                {statuses.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="py-2 px-2 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={tickets.length > 0 && selectedIds.length === tickets.length}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                    />
                  </th>
                  <th className="text-left py-2">User</th>
                  <th className="text-left py-2">Query</th>
                  <th className="text-left py-2">Feedback</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Created</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {loadingTickets ? (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading tickets...
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-gray-500">No tickets found</td></tr>
                ) : tickets.map((item) => {
                  const isSelected = selectedIds.includes(item._id);
                  return (
                  <tr key={item._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : ""}`}>
                    <td className="py-3 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelectRow(item._id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                      />
                    </td>
                    <td className="py-3 pr-3 text-sm text-gray-700 dark:text-gray-200">
                      <div>{item.user?.name || "Guest"}</div>
                      {item.user?.supportTicketsCount !== undefined && (
                        <div className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mt-0.5">
                          {item.user.supportTicketsCount} tickets
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-sm text-gray-700 dark:text-gray-200 max-w-md">
                      <p className="line-clamp-2">{item.query}</p>
                    </td>
                    <td className="py-3 pr-3 text-sm text-gray-700 dark:text-gray-200">
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{item.feedback}</p>
                    </td>
                    <td className="py-3 pr-3">
                      <select
                        value={item.status || "open"}
                        onChange={(e) => handleStatusUpdate(item._id, e.target.value)}
                        className="px-2 py-1 rounded border text-gray-500 border-gray-300 dark:border-slate-600 bg-transparent text-sm"
                      >
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3 pr-3 text-sm text-gray-500">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-500">
            <button
              onClick={() => fetchTickets(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-gray-300 dark:border-slate-600 disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page} / {totalPages}</span>
            <button
              onClick={() => fetchTickets(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 dark:border-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={tickets.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="support tickets"
      />
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{value || 0}</p>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false }) {
  return (
    <label className="block text-sm text-gray-700 dark:text-gray-300 space-y-1">
      <span>{label}</span>
      {multiline ? (
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
        />
      )}
    </label>
  );
}
